/**
 * The sliding-puzzle rules, ported from `apps/puzzletypes/fifteen_puzzle/tray.py`. The two
 * are kept at parity by the shared fixtures in `shared/fixtures/fifteen_puzzle/`.
 *
 * Pure: it reads tiles and moves and returns trays. A move names the direction a *tile*
 * travels into the blank — `u` slides the tile below the blank up — so the blank goes the
 * opposite way.
 */
import { BLANK, type Direction } from "./types";

/** Where the blank goes when a tile slides in the named direction. */
export const BLANK_STEP: Record<Direction, [number, number]> = {
  u: [1, 0],
  d: [-1, 0],
  l: [0, 1],
  r: [0, -1],
};

export function isDirection(value: string): value is Direction {
  return value === "u" || value === "d" || value === "l" || value === "r";
}

/** The solved tray: tiles in reading order, the blank in the last cell. */
export function goal(size: number): number[] {
  return [...Array.from({ length: size * size - 1 }, (_, i) => i + 1), BLANK];
}

export function isPermutation(tiles: number[], size: number): boolean {
  if (tiles.length !== size * size) return false;
  const sorted = [...tiles].sort((a, b) => a - b);
  return sorted.every((value, index) => value === index);
}

/** Parity of the permutation taking the goal to `tiles`, the blank counted as a tile. */
export function permutationParity(tiles: number[]): number {
  const count = tiles.length;
  const target = tiles.map((value) => (value === BLANK ? count - 1 : value - 1));
  const seen = new Array<boolean>(count).fill(false);
  let swaps = 0;
  for (let index = 0; index < count; index += 1) {
    if (seen[index]) continue;
    let length = 0;
    let cursor = index;
    while (!seen[cursor]) {
      seen[cursor] = true;
      cursor = target[cursor];
      length += 1;
    }
    swaps += length - 1;
  }
  return swaps % 2;
}

/**
 * Every slide is one transposition and one step of the blank, so the two parities change
 * together: a tray is reachable from the goal exactly when they agree.
 */
export function isSolvable(tiles: number[], size: number): boolean {
  const blank = tiles.indexOf(BLANK);
  const row = Math.floor(blank / size);
  const col = blank % size;
  const distance = size - 1 - row + (size - 1 - col);
  return permutationParity(tiles) === distance % 2;
}

/** One slide, or `null` when no tile sits on that side of the blank. */
export function slide(tiles: number[], size: number, move: string): number[] | null {
  if (!isDirection(move)) return null;
  const [dr, dc] = BLANK_STEP[move];
  const blank = tiles.indexOf(BLANK);
  const row = Math.floor(blank / size) + dr;
  const col = (blank % size) + dc;
  if (row < 0 || col < 0 || row >= size || col >= size) return null;
  const source = row * size + col;
  const next = [...tiles];
  next[blank] = next[source];
  next[source] = BLANK;
  return next;
}

export interface Replay {
  tiles: number[];
  /** The moves actually played (a prefix of the input). */
  moves: string;
  /** Index of the first move that could not be played, or `null` when all were legal. */
  illegalAt: number | null;
}

/** Play a move string from `tiles`, stopping at the first illegal move. */
export function replay(tiles: number[], size: number, moves: string): Replay {
  let state = tiles;
  for (let index = 0; index < moves.length; index += 1) {
    const next = slide(state, size, moves[index]);
    if (!next) return { tiles: state, moves: moves.slice(0, index), illegalAt: index };
    state = next;
  }
  return { tiles: state, moves, illegalAt: null };
}

export function isSolved(tiles: number[], size: number): boolean {
  const ordered = goal(size);
  return tiles.length === ordered.length && tiles.every((value, index) => value === ordered[index]);
}

/** How many numbered tiles already sit in their goal cell — read off the tray. */
export function tilesHome(tiles: number[]): number {
  return tiles.filter((value, index) => value !== BLANK && value === index + 1).length;
}

/** Sum of every tile's distance to its goal cell: no solution is shorter. Not a solver. */
export function manhattan(tiles: number[], size: number): number {
  let total = 0;
  tiles.forEach((value, index) => {
    if (value === BLANK) return;
    total +=
      Math.abs(Math.floor(index / size) - Math.floor((value - 1) / size)) +
      Math.abs((index % size) - ((value - 1) % size));
  });
  return total;
}

/**
 * The slides that bring the tile at `index` to the blank, pushing the tiles between along:
 * a tap on any tile in the blank's row or column. Empty when the tile is not in line.
 */
export function slidesToward(tiles: number[], size: number, index: number): string {
  const blank = tiles.indexOf(BLANK);
  const [br, bc] = [Math.floor(blank / size), blank % size];
  const [r, c] = [Math.floor(index / size), index % size];
  if (index === blank || index < 0 || index >= tiles.length) return "";
  if (r === br) return (c > bc ? "l" : "r").repeat(Math.abs(c - bc));
  if (c === bc) return (r > br ? "u" : "d").repeat(Math.abs(r - br));
  return "";
}
